import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut, apiDel } from '../lib/api.js';
import TopBar from '../components/TopBar.jsx';
import { MessageSquare, Plus, Trash2, Save, Beaker } from 'lucide-react';
import toast from 'react-hot-toast';

const MODES = [
  { value: 'exact', label: 'Exact', desc: 'Body equals keyword (case-insensitive by default)' },
  { value: 'starts', label: 'Starts with', desc: 'Body starts with the keyword' },
  { value: 'contains', label: 'Contains', desc: 'Keyword appears anywhere in body' },
  { value: 'regex', label: 'Regex', desc: 'Advanced JS regular expression' },
];

export default function SmsKeywords() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['sms-keywords'], queryFn: () => apiGet('/sms-keywords') });
  const items = data?.items || [];
  return (
    <div className="space-y-4">
      <TopBar title="SMS Keywords" subtitle="Auto-respond + tag inbound texts" />
      <NewKeywordForm onCreated={() => qc.invalidateQueries({ queryKey: ['sms-keywords'] })} />
      <KeywordTester />
      <div className="space-y-2">
        {items.length === 0 && <div className="panel p-6 text-white/50 text-sm">No keywords yet.</div>}
        {items.map((k) => <KwRow key={k._id} k={k} onChanged={() => qc.invalidateQueries({ queryKey: ['sms-keywords'] })} />)}
      </div>
    </div>
  );
}

function NewKeywordForm({ onCreated }) {
  const [keyword, setKeyword] = useState('');
  const [matchMode, setMatchMode] = useState('exact');
  const [autoReplyBody, setAutoReplyBody] = useState('');
  const [busy, setBusy] = useState(false);
  async function submit(e) {
    e.preventDefault();
    if (!keyword.trim()) return toast.error('Keyword required');
    setBusy(true);
    try { await apiPost('/sms-keywords', { keyword: keyword.trim(), matchMode, autoReplyBody: autoReplyBody.trim() || undefined, enabled: true, priority: 50 }); toast.success('Created'); setKeyword(''); setAutoReplyBody(''); onCreated(); }
    catch (err) { toast.error(err.message); } finally { setBusy(false); }
  }
  return (
    <form onSubmit={submit} className="panel p-4 grid md:grid-cols-12 gap-3 items-end">
      <div className="md:col-span-3">
        <label className="block text-xs text-white/60 mb-1">Keyword</label>
        <input value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="INFO" className="input w-full" />
      </div>
      <div className="md:col-span-3">
        <label className="block text-xs text-white/60 mb-1">Match mode</label>
        <select value={matchMode} onChange={(e) => setMatchMode(e.target.value)} className="input w-full">
          {MODES.map((m) => <option key={m.value} value={m.value}>{m.label} — {m.desc}</option>)}
        </select>
      </div>
      <div className="md:col-span-4">
        <label className="block text-xs text-white/60 mb-1">Auto-reply (optional)</label>
        <input value={autoReplyBody} onChange={(e) => setAutoReplyBody(e.target.value)} placeholder="Thanks! An agent will reach out shortly." className="input w-full" />
      </div>
      <button disabled={busy} className="btn-primary inline-flex items-center justify-center gap-2 md:col-span-2"><Plus className="w-4 h-4" /> Add</button>
    </form>
  );
}

function KwRow({ k, onChanged }) {
  const [edit, setEdit] = useState({ ...k });
  const [dirty, setDirty] = useState(false);
  function set(field, value) { setEdit({ ...edit, [field]: value }); setDirty(true); }
  async function save() { try { await apiPut(`/sms-keywords/${k._id}`, edit); toast.success('Saved'); setDirty(false); onChanged(); } catch (e) { toast.error(e.message); } }
  async function del() { if (!confirm('Delete this keyword?')) return; await apiDel(`/sms-keywords/${k._id}`); onChanged(); }
  return (
    <div className="panel p-3 grid md:grid-cols-12 gap-2 items-center text-sm">
      <input value={edit.keyword} onChange={(e) => set('keyword', e.target.value)} className="input md:col-span-2" />
      <select value={edit.matchMode} onChange={(e) => set('matchMode', e.target.value)} className="input md:col-span-2">
        {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
      </select>
      <input value={edit.autoReplyBody || ''} onChange={(e) => set('autoReplyBody', e.target.value)} placeholder="auto-reply" className="input md:col-span-4" />
      <input value={edit.setStatus || ''} onChange={(e) => set('setStatus', e.target.value)} placeholder="set status" className="input md:col-span-2" />
      <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={edit.enabled} onChange={(e) => set('enabled', e.target.checked)} /> Enabled</label>
      <div className="text-xs text-white/50">Hit: {k.matchCount || 0}</div>
      <div className="md:col-span-12 flex justify-end gap-2">
        {dirty && <button onClick={save} className="btn-primary inline-flex items-center gap-1 text-xs"><Save className="w-3 h-3" /> Save</button>}
        <button onClick={del} className="text-white/40 hover:text-rose-400 text-xs inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</button>
      </div>
    </div>
  );
}

function KeywordTester() {
  const [body, setBody] = useState('');
  const [matches, setMatches] = useState(null);
  async function run() { try { const r = await apiPost('/sms-keywords/test', { body }); setMatches(r.matches || []); } catch (e) { toast.error(e.message); } }
  return (
    <div className="panel p-4 space-y-2">
      <div className="flex items-center gap-2 text-sm text-white/70"><Beaker className="w-4 h-4" /> Test inbound</div>
      <div className="flex gap-2">
        <input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Type a sample inbound text..." className="input flex-1" />
        <button onClick={run} className="btn">Test</button>
      </div>
      {matches !== null && (
        <div className="text-xs text-white/60">{matches.length === 0 ? 'No keyword matched.' : `Matched: ${matches.map((m) => `${m.keyword} (${m.matchMode})`).join(', ')}`}</div>
      )}
    </div>
  );
}
